import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EditProfileForm from "../../components/profile/edit/EditProfileForm";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

import {
  getUserDetails,
  updateUserProfile,
  updateUserProfileImage,
} from "../../actions/userActions";
import {
  USER_UPDATE_PROFILE_RESET,
  USER_UPDATE_PROFILE_IMAGE_RESET,
} from "../../constants/userConstants";
import { isProfileIncomplete, selectIdentity } from "../../selectors/userSelectors";
import {
  createSubmitCycle,
  getSubmitCycleFeedback,
  markSubmitCycleEvent,
} from "./profileEditSubmitFeedback";

const HEADER_COPY = {
  edit: {
    title: "Редагувати профіль",
    subtitle:
      "Оновіть особисті дані, контакти та посилання на соціальні мережі. Зміни буде видно іншим користувачам одразу після збереження.",
  },
  onboarding: {
    title: "Завершити авторизацію",
    subtitle:
      "Заповніть кілька обовʼязкових полів — імʼя, прізвище та посилання на профіль — щоб почати користуватись Рієстро. Це займе хвилину.",
  },
};

function ProfileEditScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // R5.8-i (partial): identity-presence guard reads via memoized selectIdentity
  // (primitive-only, auth-adjacent, stable). The legacy state.userLogin
  // subscription is preserved verbatim BELOW because:
  //   (a) isProfileIncomplete(userInfo) consumes profile-owned fields
  //       (first_name, last_name, handle) — profile semantics are NOT in
  //       R5.8-i scope; only stable identity primitive extraction is.
  //   (b) The publicId mismatch check on Effect 1 encodes ownership-sensitive
  //       routing/stale-profile semantics and is intentionally left untouched
  //       this phase. Its existing trigger cadence depends on `userInfo` ref
  //       churn, which is preserved by keeping `userInfo` in Effect 1 deps.
  const identity = useSelector(selectIdentity);
  const { userInfo } = useSelector((state) => state.userLogin);
  const {
    user,
    loading: loadingUser,
    error: errorUser,
  } = useSelector((state) => state.userDetails);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
    fieldErrors: updateFieldErrors,
  } = useSelector((state) => state.userUpdateProfile);
  const {
    loading: loadingImage,
    error: errorImage,
    success: successImage,
  } = useSelector((state) => state.userUpdateProfileImage);

  const onboardingMode = isProfileIncomplete(userInfo);
  const copy = onboardingMode ? HEADER_COPY.onboarding : HEADER_COPY.edit;

  const submitCycleRef = useRef(createSubmitCycle({ hasImage: false, wasOnboarding: false }));
  const feedbackShownRef = useRef(false);
  const profileFetchKeyRef = useRef(null);

  const tryShowSubmitFeedback = useCallback(() => {
    if (feedbackShownRef.current) return;

    const feedback = getSubmitCycleFeedback(submitCycleRef.current);
    if (!feedback) return;

    feedbackShownRef.current = true;

    if (feedback.kind === "success") {
      toast.success(
        submitCycleRef.current.wasOnboarding
          ? "Вітаємо у Рієстро! Профіль готовий."
          : "Профіль успішно оновлено!"
      );

      if (feedback.shouldNavigate) {
        navigate(
          submitCycleRef.current.wasOnboarding ? "/" : "/profile",
          { replace: true },
        );
      }
      return;
    }

    if (feedback.kind === "warning") {
      toast.warning("Профіль збережено, але фото не вдалося завантажити.");
    }
  }, [navigate]);

  useEffect(() => {
    if (!identity) {
      navigate("/login");
      return;
    }
    const identityKey = String(userInfo?.public_id || identity.userId || "");
    if (user && userInfo?.public_id === user.public_id) {
      profileFetchKeyRef.current = identityKey;
      return;
    }
    if (loadingUser) {
      return;
    }
    if (profileFetchKeyRef.current === identityKey) {
      return;
    }
    profileFetchKeyRef.current = identityKey;
    dispatch(getUserDetails("profile"));
  }, [dispatch, navigate, identity, userInfo?.public_id, user, loadingUser]);

  useEffect(() => {
    if (!successUpdate) return;

    submitCycleRef.current = markSubmitCycleEvent(
      submitCycleRef.current,
      "profileSuccess"
    );
    dispatch({ type: USER_UPDATE_PROFILE_RESET });
    dispatch(getUserDetails("profile"));
    tryShowSubmitFeedback();
  }, [dispatch, successUpdate, tryShowSubmitFeedback]);

  useEffect(() => {
    if (!errorUpdate) return;

    submitCycleRef.current = markSubmitCycleEvent(
      submitCycleRef.current,
      "profileError"
    );
    tryShowSubmitFeedback();
  }, [errorUpdate, tryShowSubmitFeedback]);

  useEffect(() => {
    if (!successImage) return;

    submitCycleRef.current = markSubmitCycleEvent(
      submitCycleRef.current,
      "imageSuccess"
    );
    dispatch(getUserDetails("profile"));
    dispatch({ type: USER_UPDATE_PROFILE_IMAGE_RESET });
    tryShowSubmitFeedback();
  }, [dispatch, successImage, tryShowSubmitFeedback]);

  useEffect(() => {
    if (!errorImage) return;

    submitCycleRef.current = markSubmitCycleEvent(
      submitCycleRef.current,
      "imageError"
    );
    tryShowSubmitFeedback();
  }, [errorImage, tryShowSubmitFeedback]);

  const handleSubmit = (formData) => {
    const { user_image, ...rest } = formData;

    submitCycleRef.current = createSubmitCycle({
      hasImage: Boolean(user_image),
      wasOnboarding: onboardingMode,
    });
    feedbackShownRef.current = false;

    dispatch(updateUserProfile(rest));

    if (user_image) {
      const fd = new FormData();
      fd.append("user_image", user_image);
      dispatch(updateUserProfileImage(fd));
    }
  };

  const isLoadingInitial = loadingUser && !user;
  const submitting = loadingUpdate || loadingImage;

  return (
    <main
      className="add-property-page"
      id="Csection"
      tabIndex="-1"
      data-page="edit-profile"
    >
      <div className="container">
        <header
          className="ap-header"
          data-section="page-header"
          data-mode={onboardingMode ? "onboarding" : "edit"}
        >
          {!onboardingMode && (
            <Link to="/" className="cd-back">
              <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
              До профілю
            </Link>
          )}
          <div className="ap-header__row">
            <div className="ap-header__text">
              <h1>{copy.title}</h1>
              <p>{copy.subtitle}</p>
            </div>
            {!onboardingMode && (
              <div
                className="ap-header__status"
                data-profile-status="active"
              >
                <span className="status-pill status-pill--lead">
                  <span className="dot" aria-hidden="true"></span>Активний
                </span>
                <small>Видно у пошуку</small>
              </div>
            )}
          </div>
        </header>

        {errorUpdate && typeof errorUpdate === "string" ? (
          <Message variant="danger">{errorUpdate}</Message>
        ) : null}

        {isLoadingInitial ? (
          <Loader />
        ) : errorUser ? (
          <Message variant="danger">{errorUser}</Message>
        ) : (
          <EditProfileForm
            initialData={user}
            onSubmit={handleSubmit}
            submitting={submitting}
            onboardingMode={onboardingMode}
            serverHandleError={updateFieldErrors?.handle || null}
            serverFieldErrors={updateFieldErrors || null}
            photoUploadStatus={{
              loading: loadingImage,
              success: successImage,
              error: errorImage,
            }}
          />
        )}
      </div>
    </main>
  );
}

export default ProfileEditScreen;
